/**
 * CR552: Admin IA restructure — Runs → Builder → Extraction Builder → Merchants → Tools → Policy → Flyers
 * Max 5–6 items per heading. Renamed "Prompt Pack Builder" → "Extraction Builder", "Recipes" → "Extraction Recipes"
 */

import {
  Calendar,
  Layers,
  Globe,
  Play,
  LayoutGrid,
  Palette,
  Package,
  FileText,
  Brain,
  Database,
  BarChart3,
  Activity,
  Camera,
  Bot,
  ScanLine,
  Wrench,
  Home,
  Settings,
  History,
  Shield,
  FolderArchive,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  title: string;
  href: string;
  icon: LucideIcon;
}

export interface NavSection {
  sectionKey: string;
  sectionLabel: string;
  sectionIcon: LucideIcon;
  items: NavItem[];
}

export const adminNavSections: NavSection[] = [
  // CR552: Runs (max 4 items) — Live Preview first
  {
    sectionKey: "runs",
    sectionLabel: "Runs",
    sectionIcon: Play,
    items: [
      { title: "Live Preview", href: "/admin/live-preview", icon: Play },
      { title: "Scheduler", href: "/admin/scheduler", icon: Calendar },
      { title: "Batch Runs", href: "/admin/batch-runs", icon: Layers },
      { title: "Website Runs", href: "/admin/poc-runs", icon: Globe },
    ],
  },
  {
    sectionKey: "dashboards",
    sectionLabel: "Dashboards",
    sectionIcon: BarChart3,
    items: [
      { title: "Executive Summary", href: "/admin/dashboards/executive-summary", icon: BarChart3 },
    ],
  },
  // CR552: Builder (max 3 items)
  {
    sectionKey: "builder",
    sectionLabel: "Builder",
    sectionIcon: LayoutGrid,
    items: [
      { title: "Builder Dashboard", href: "/admin/builder", icon: Home },
      { title: "Layout Studio", href: "/admin/layout-studio", icon: LayoutGrid },
      { title: "Theme Labs", href: "/admin/theme-labs", icon: Palette },
      { title: "Deal Cards", href: "/admin/command-center", icon: LayoutGrid },
      { title: "Asset Studio", href: "/admin/asset-studio", icon: Palette },
    ],
  },
  // CR552: Extraction Builder (renamed from Prompt Pack Builder)
  {
    sectionKey: "extraction-builder",
    sectionLabel: "Extraction Builder",
    sectionIcon: Package,
    items: [
      { title: "Prompt Packs", href: "/admin/prompt-packs", icon: Package },
      { title: "Prompt Library", href: "/admin/prompts-library", icon: FileText },
      { title: "Extraction Recipes", href: "/admin/recipes", icon: Globe },
    ],
  },
  // CR552: Merchants (max 6 items)
  {
    sectionKey: "merchants",
    sectionLabel: "Merchants",
    sectionIcon: Database,
    items: [
      { title: "Directory", href: "/admin/merchants/directory", icon: Database },
      { title: "Categories", href: "/admin/merchants/categories", icon: Database },
      { title: "Sources", href: "/admin/merchant-sources", icon: Database },
      { title: "Source Health", href: "/admin/source-health", icon: Activity },
      { title: "Issuer Needs-Split", href: "/admin/merchants/issuer-needs-split", icon: Activity },
      { title: "Image Capture", href: "/admin/image-capture", icon: ScanLine },
    ],
  },
  // CR552: Tools (max 4 items)
  {
    sectionKey: "tools",
    sectionLabel: "Tools",
    sectionIcon: Wrench,
    items: [
      { title: "Production Tools", href: "/admin/production-tools", icon: Wrench },
      { title: "AI Providers & Budgets", href: "/admin/ai-providers", icon: Brain },
      { title: "Forum Tools", href: "/admin/forum-tools", icon: Database },
      { title: "Community Signal Diagnostics", href: "/admin/forum-deals", icon: Activity },
    ],
  },
  // CR552: Policy (max 4 items)
  {
    sectionKey: "policy",
    sectionLabel: "Policy",
    sectionIcon: Shield,
    items: [
      { title: "Deal Policy (Rules)", href: "/admin/deal-policy", icon: FileText },
      { title: "Pipeline Tolerances", href: "/admin/pipeline-tolerances", icon: Settings },
      { title: "Gate Patterns", href: "/admin/gate-patterns", icon: Shield },
      { title: "Audit Log", href: "/admin/audit-log", icon: History },
      { title: "Feature Flags", href: "/admin/feature-flags", icon: Settings },
    ],
  },
  // CR552: Flyers (last, max 4 items)
  {
    sectionKey: "flyers",
    sectionLabel: "Flyers",
    sectionIcon: FolderArchive,
    items: [
      { title: "Flyer Bucket Sources", href: "/admin/flyer-bucket-sources", icon: FileText },
      { title: "Flyer Capture Console", href: "/admin/flyer-capture-console", icon: Camera },
      { title: "Flyer Diagnostics v2", href: "/admin/flyer-diagnostics-v2", icon: Bot },
      { title: "Storage Manager", href: "/admin/storage-manager", icon: Database },
    ],
  },
];

/**
 * Get the section key that contains the current path
 */
export function getActiveSectionKey(pathname: string): string | null {
  for (const section of adminNavSections) {
    for (const item of section.items) {
      // Handle both exact matches and query param routes
      const baseHref = item.href.split("?")[0];
      if (pathname === baseHref || pathname.startsWith(baseHref + "/")) {
        return section.sectionKey;
      }
    }
  }
  return null;
}

/**
 * Check if a nav item is active based on current path
 */
export function isNavItemActive(pathname: string, search: string, itemHref: string): boolean {
  const [baseHref, queryPart] = itemHref.split("?");
  
  // Check base path match
  const pathMatches = pathname === baseHref || pathname.startsWith(baseHref + "/");
  
  // If there's a query param in the href, check that too
  if (queryPart && pathMatches) {
    const params = new URLSearchParams(queryPart);
    const currentParams = new URLSearchParams(search);
    for (const [key, value] of params.entries()) {
      if (currentParams.get(key) !== value) {
        return false;
      }
    }
    return true;
  }
  
  // For items without query params, only match if current URL also has no matching query param routes
  if (!queryPart && pathMatches) {
    // Check if another item in the same section with query params should be active instead
    const section = adminNavSections.find(s => 
      s.items.some(i => i.href.split("?")[0] === baseHref)
    );
    if (section) {
      const queryParamItem = section.items.find(i => {
        const [iBase, iQuery] = i.href.split("?");
        if (iBase === baseHref && iQuery) {
          const params = new URLSearchParams(iQuery);
          const currentParams = new URLSearchParams(search);
          for (const [key, value] of params.entries()) {
            if (currentParams.get(key) === value) {
              return true;
            }
          }
        }
        return false;
      });
      if (queryParamItem) {
        return false; // Another item with query params is active
      }
    }
    return true;
  }
  
  return false;
}
